import { uiSurface } from "shared/ui/uiSurface"
import { Box, Button, Chip, Typography } from "@mui/material"
import {
  APIProvider,
  AdvancedMarker,
  InfoWindow,
  Map,
  Pin,
} from "@vis.gl/react-google-maps"
import { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { DashboardOrder, useGetProviderDetailQuery } from "../../apis/appApi"
import { stateToGroupKey, statusGroups } from "./ordersPageConfig"

const MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? ""
const DEFAULT_CENTER = { lat: 45.400536, lng: -71.896209 }
const DEFAULT_ZOOM = 12

type ProviderOrderMapProps = {
  orders: DashboardOrder[]
  selectedGroup: string | null
}

/** Map of status group key -> hex color from ordersPageConfig */
const groupColorMap: Record<string, string> = {}
for (const g of statusGroups) {
  groupColorMap[g.key] = g.color
}

function getOrderColor(state: string): string {
  const groupKey = stateToGroupKey[state]
  return groupColorMap[groupKey] ?? "#757575"
}

export const ProviderOrderMap: FC<ProviderOrderMapProps> = ({
  orders,
  selectedGroup,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: provider } = useGetProviderDetailQuery()

  const [selectedOrder, setSelectedOrder] = useState<DashboardOrder | null>(
    null,
  )

  // Provider HQ position
  const providerPosition = useMemo(() => {
    const pos = provider?.address?.position
    if (pos) return { lat: pos.latitude, lng: pos.longitude }
    return null
  }, [provider])

  // Orders that have position data
  const mappableOrders = useMemo(
    () => orders.filter((o) => o.address?.position),
    [orders],
  )

  const unmappedCount = orders.length - mappableOrders.length

  // Determine map center from provider position or first order with position
  const mapCenter = useMemo(() => {
    if (providerPosition) return providerPosition
    if (mappableOrders.length > 0) {
      const pos = mappableOrders[0].address!.position!
      return { lat: pos.latitude, lng: pos.longitude }
    }
    return DEFAULT_CENTER
  }, [providerPosition, mappableOrders])

  const handleMarkerClick = useCallback(
    (order: DashboardOrder) => {
      setSelectedOrder(order)
    },
    [],
  )

  const handleViewDetail = useCallback(
    (orderId: number) => {
      navigate(`/orders/${orderId}`)
    },
    [navigate],
  )

  return (
    <APIProvider apiKey={MAPS_API_KEY}>
      <Box {...uiSurface("UI-DLV2-027")} sx={{ position: "relative", width: "100%", height: "100%" }}>
        <Map
          defaultCenter={mapCenter}
          defaultZoom={DEFAULT_ZOOM}
          mapId="provider-order-map"
          gestureHandling="greedy"
          disableDefaultUI={false}
          style={{ width: "100%", height: "100%" }}
        >
          {/* Provider HQ marker */}
          {providerPosition && (
            <AdvancedMarker position={providerPosition} zIndex={1000}>
              <Pin
                background="#009688"
                borderColor="#00695c"
                glyphColor="#ffffff"
                scale={1.3}
              />
            </AdvancedMarker>
          )}

          {/* Order markers */}
          {mappableOrders.map((order) => {
            const pos = order.address!.position!
            const color = getOrderColor(order.state)
            const isHighlighted =
              !selectedGroup || stateToGroupKey[order.state] === selectedGroup

            return (
              <AdvancedMarker
                key={order.id}
                position={{ lat: pos.latitude, lng: pos.longitude }}
                zIndex={isHighlighted ? 100 : 10}
                onClick={() => handleMarkerClick(order)}
              >
                <Pin
                  background={color}
                  borderColor={color}
                  glyphColor="#ffffff"
                  scale={isHighlighted ? 1.1 : 0.8}
                />
              </AdvancedMarker>
            )
          })}

          {/* InfoWindow for selected order */}
          {selectedOrder?.address?.position && (
            <InfoWindow
              position={{
                lat: selectedOrder.address.position.latitude,
                lng: selectedOrder.address.position.longitude,
              }}
              onCloseClick={() => setSelectedOrder(null)}
            >
              <Box sx={{ minWidth: 180, p: 0.5 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {selectedOrder.client
                    ? `${selectedOrder.client.firstName} ${selectedOrder.client.lastName}`
                    : `#${selectedOrder.id}`}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {selectedOrder.reference}
                </Typography>
                {selectedOrder.address && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {selectedOrder.address.streetNumber}{" "}
                    {selectedOrder.address.streetName},{" "}
                    {selectedOrder.address.city}
                  </Typography>
                )}
                <Chip
                  label={selectedOrder.state.replace(/_/g, " ")}
                  size="small"
                  sx={{
                    mt: 0.5,
                    bgcolor: getOrderColor(selectedOrder.state),
                    color: "#fff",
                    fontSize: 10,
                    height: 20,
                  }}
                />
                <Button
                  size="small"
                  variant="text"
                  onClick={() => handleViewDetail(selectedOrder.id)}
                  sx={{ mt: 0.5, display: "block", fontSize: 11, p: 0 }}
                >
                  {t("ordersPage.viewDetail")}
                </Button>
              </Box>
            </InfoWindow>
          )}
        </Map>

        {/* Legend overlay */}
        <Box
          sx={{
            position: "absolute",
            bottom: 24,
            left: 12,
            bgcolor: "rgba(255,255,255,0.92)",
            borderRadius: 1,
            px: 1.5,
            py: 1,
            boxShadow: 1,
            maxWidth: 180,
          }}
        >
          <Typography
            variant="caption"
            fontWeight={600}
            display="block"
            sx={{ mb: 0.5 }}
          >
            {t("ordersPage.map.legend")}
          </Typography>
          {statusGroups.map((g) => (
            <Box
              key={g.key}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                py: 0.15,
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: g.color,
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
                {t(g.labelKey)}
              </Typography>
            </Box>
          ))}
          {/* Provider HQ entry */}
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.75, py: 0.15 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: "#009688",
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
              {t("ordersPage.map.providerHq")}
            </Typography>
          </Box>
        </Box>

        {/* Unmapped orders chip */}
        {unmappedCount > 0 && (
          <Chip
            label={t("ordersPage.map.unmappedOrders", { count: unmappedCount })}
            size="small"
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              bgcolor: "rgba(255,255,255,0.92)",
              boxShadow: 1,
              fontSize: 11,
            }}
          />
        )}
      </Box>
    </APIProvider>
  )
}
